// Nominatim search & OSRM routing (both public APIs, no key required)

export async function searchPlaces(query) {
    if (!query || query.length < 3) return [];
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query,
    )}&limit=6&addressdetails=1`;
    const res = await fetch(url, {
        headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Búsqueda no disponible");
    const data = await res.json();
    return data.map((d) => ({
        id: d.place_id,
        label: d.display_name,
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
        bbox: d.boundingbox ? d.boundingbox.map(parseFloat) : null,
    }));
}

// Auto-route between waypoints along roads/trails using OSRM public demo
// profile: 'driving' | 'cycling' | 'foot'
export async function snapToRoads(points, profile = "driving") {
    if (!points || points.length < 2) return null;
    const coords = points.map((p) => `${p.lng},${p.lat}`).join(";");
    const url = `https://router.project-osrm.org/route/v1/${profile}/${coords}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("OSRM no disponible");
    const data = await res.json();
    if (!data.routes || data.routes.length === 0) return null;
    const geom = data.routes[0].geometry.coordinates; // [lng, lat]
    // OSRM road routes are considered paved (asphalt).
    return geom.map(([lng, lat]) => ({ lat, lng, paved: true }));
}

const PAVED_SURFACES = new Set([
    "asphalt",
    "paved",
    "concrete",
    "concrete:lanes",
    "concrete:plates",
    "paving_stones",
    "sett",
    "cobblestone",
    "metal",
    "wood",
    "chipseal",
]);
const UNPAVED_SURFACES = new Set([
    "unpaved",
    "compacted",
    "fine_gravel",
    "gravel",
    "pebblestone",
    "ground",
    "dirt",
    "earth",
    "grass",
    "grass_paver",
    "mud",
    "sand",
    "rock",
    "stone",
    "woodchips",
    "clay",
]);
const UNPAVED_HIGHWAYS = new Set([
    "track",
    "path",
    "bridleway",
    "footway",
    "cycleway",
    "steps",
    "pedestrian",
]);

// Decide whether a BRouter way segment is paved (asphalt) from its OSM tags.
function isPavedFromTags(tags) {
    if (!tags) return true;
    const sm = /surface=([a-zA-Z_:]+)/.exec(tags);
    const surface = sm ? sm[1] : null;
    const hm = /highway=([a-zA-Z_]+)/.exec(tags);
    const highway = hm ? hm[1] : null;
    return classifyPaved(surface, highway);
}

// Shared surface classifier used by BRouter tags and Overpass tags.
export function classifyPaved(surface, highway) {
    if (surface) {
        if (PAVED_SURFACES.has(surface)) return true;
        if (UNPAVED_SURFACES.has(surface)) return false;
    }
    if (highway && UNPAVED_HIGHWAYS.has(highway)) return false;
    return true; // named roads without surface tag assumed paved
}

// Offroad auto-route using BRouter public server (no key required).
// Follows unpaved tracks / dirt roads (pistas) wide enough for a vehicle,
// avoiding motorways where possible.
// profile: 'driving' | 'cycling' | 'foot'
// grade: 1 = pistas fáciles (grava), 2 = medias, 3 = 4x4 / difícil
export async function snapOffroad(points, profile = "driving", grade = 1) {
    if (!points || points.length < 2) return null;
    // Map transport mode + difficulty to the best-matching BRouter profile.
    let brProfile;
    if (profile === "foot") brProfile = "trekking";
    else brProfile = grade === 3 ? "mtb" : grade === 2 ? "trekking" : "gravel";
    const lonlats = points.map((p) => `${p.lng},${p.lat}`).join("|");
    const url = `https://brouter.de/brouter?lonlats=${lonlats}&profile=${brProfile}&alternativeidx=0&format=geojson`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("BRouter no disponible");
    const text = await res.text();
    let data;
    try {
        data = JSON.parse(text);
    } catch {
        // BRouter returns a plain-text error like "no track found at pass=0"
        throw new Error(text.trim() || "No se encontró ruta offroad");
    }
    const feat = data.features && data.features[0];
    if (!feat || !feat.geometry) return null;
    const geom = feat.geometry.coordinates; // [lng, lat, ele?]

    // Build ordered way segments (with surface) from the messages table.
    const msgs = feat.properties && feat.properties.messages;
    const segs = [];
    if (Array.isArray(msgs) && msgs.length > 1) {
        for (let i = 1; i < msgs.length; i++) {
            const row = msgs[i];
            segs.push({
                lng: parseInt(row[0], 10) / 1e6,
                lat: parseInt(row[1], 10) / 1e6,
                paved: isPavedFromTags(row[9]),
            });
        }
    }

    // Walk geometry, attributing each vertex to the current way segment.
    let si = 0;
    return geom.map((c) => {
        const lng = c[0];
        const lat = c[1];
        const paved =
            segs.length === 0
                ? true
                : si < segs.length
                  ? segs[si].paved
                  : segs[segs.length - 1].paved;
        const pt = { lat, lng, paved };
        if (typeof c[2] === "number") pt.ele = c[2];
        if (
            si < segs.length &&
            Math.abs(lng - segs[si].lng) < 1e-5 &&
            Math.abs(lat - segs[si].lat) < 1e-5
        ) {
            si++;
        }
        return pt;
    });
}


// Analyse the surface (paved vs unpaved) of an EXISTING track (imported or
// manually planned) by map-matching each point to the nearest OSM highway via
// the Overpass API. Returns a new points array with a `paved` flag per point
// WITHOUT changing the geometry. Points farther than ~40 m from any road keep
// their previous flag (or undefined).
export async function analyzeSurface(points) {
    if (!points || points.length < 2) return null;

    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    const pad = 0.004; // ~400 m
    const s = Math.min(...lats) - pad;
    const n = Math.max(...lats) + pad;
    const w = Math.min(...lngs) - pad;
    const e = Math.max(...lngs) + pad;

    // Guard against absurdly large areas (Overpass would time out).
    if (n - s > 1.2 || e - w > 1.2) {
        throw new Error("La ruta abarca un área demasiado grande para analizar");
    }

    const query = `[out:json][timeout:25];way["highway"](${s},${w},${n},${e});out geom tags;`;
    const endpoints = [
        "https://overpass-api.de/api/interpreter",
        "https://overpass.kumi.systems/api/interpreter",
        "https://overpass.private.coffee/api/interpreter",
        "https://maps.mail.ru/osm/tools/overpass/api/interpreter",
    ];
    let data = null;
    let lastErr = null;
    for (const url of endpoints) {
        try {
            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: "data=" + encodeURIComponent(query),
            });
            if (!res.ok) throw new Error("Overpass HTTP " + res.status);
            data = await res.json();
            break;
        } catch (err) {
            lastErr = err;
        }
    }
    if (!data) throw new Error("Overpass no disponible: " + (lastErr?.message || ""));

    // Build a flat list of way segments with their paved classification.
    const midLat = (s + n) / 2;
    const mPerDegLat = 111320;
    const mPerDegLng = 111320 * Math.cos((midLat * Math.PI) / 180);
    const toXY = (lat, lng) => ({ x: lng * mPerDegLng, y: lat * mPerDegLat });

    const segs = [];
    for (const el of data.elements || []) {
        const geom = el.geometry;
        if (!geom || geom.length < 2) continue;
        const paved = classifyPaved(el.tags?.surface, el.tags?.highway);
        for (let i = 1; i < geom.length; i++) {
            const a = toXY(geom[i - 1].lat, geom[i - 1].lon);
            const b = toXY(geom[i].lat, geom[i].lon);
            segs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, paved });
        }
    }
    if (segs.length === 0) throw new Error("No se encontraron vías cercanas");

    const THRESH = 40; // meters
    let matched = 0;
    const out = points.map((p) => {
        const q = toXY(p.lat, p.lng);
        let best = Infinity;
        let bestPaved = null;
        for (const sg of segs) {
            const d = distPointToSeg(q.x, q.y, sg.ax, sg.ay, sg.bx, sg.by);
            if (d < best) {
                best = d;
                bestPaved = sg.paved;
            }
        }
        if (best <= THRESH && bestPaved !== null) {
            matched++;
            return { ...p, paved: bestPaved };
        }
        return { ...p, paved: p.paved };
    });

    return { points: out, matched, total: points.length };
}

function distPointToSeg(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
